import React, { useEffect, useRef, useState } from 'react';
import TextInput from 'modules/shared/components/text-input';
import t from 'utils/translate';
import { Box } from '@mui/system';
import {
    getSearchInputData,
    updateSearchInputData,
    getUserDetails,
    getContact,
    requestSearchInputPage,
} from '../stores/contacts-slice-handler';
import { useSelector } from 'react-redux';
import Preloader from 'modules/shared/components/preloader';
import ContactUserItem from './contact-user-item';
import classNames from 'classnames';
import {
    contactsIdsListIsEmpty,
    contactsRecordsOnPage,
    createEmptyContactsIdsList,
    searchInputHasNoResults,
} from '../../../utils/contact-util';
import ContactGroupItem from './contact-group-item';
import Pagination from '@mui/material/Pagination';

/**
 * This component is used to perform and handle the results of contact based searches.
 * @param {string} uuid The uuid generated by the store when the input has been registered.
 * @param {'subscribers'|'groups'} contactType What to search.
 * @param {boolean} onlyHumans Restrict the search to human users only.
 * @param {boolean} renderContacts If the component should render the contacts on its own, or will just perform the searches and update the store with the findings.
 * @param {(contact: CugContact) => void} onContactSelected The function to be called when a selection is made. Applicable only for the case when "renderContacts" is true.
 * @param {() => void} onContactReset The function to be called when the user has reset the selected contact. Applicable only for the case when "renderContacts" is true. (optional)
 * @param {boolean} disabled The search input is disabled.
 * @param {string} selectedContactId The the id of the contact which should be selected by default. It works only if renderContacts is true.
 * @param {boolean} mandatory Empty selection leads to error mode display. It works only if renderContacts is true. (optional)
 * @param {string} placeholder What placeholder to use. (optional)
 * @param {boolean} applyUsersListFilters If the restrictions set in the UsersList filters will be applied in the search (optional) Default is FALSE.
 * @param {number} recordsOnPage A customized number for the records on page.
 * @param {function} onReset A function called when the search has been reset. (optional)
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactSearchInput({
    uuid,
    contactType,
    onlyHumans,
    renderContacts,
    onContactSelected,
    onContactReset,
    disabled,
    selectedContactId,
    mandatory,
    placeholder,
    applyUsersListFilters,
    recordsOnPage,
    onReset,
}) {
    // validate options
    if (!!renderContacts && !onContactSelected) {
        throw 'ContactSearchInput: The option "onContactSelected" is mandatory.';
    }

    const [keyPressTimer, setKeyPressTimer] = useState(null);
    const [internalSelectedContact, setInternalSelectedContact] = useState(null);
    const [hasError, setHasError] = useState(false);

    const storeSearchInputData = useSelector(() => getSearchInputData(uuid));
    /**
     *
     * @type {ContactsSearchInputData}
     */
    const defaultData = {
        contactType: contactType,
        keyword: '',
        userIsTyping: false,
        requestInProgress: false,
        matches: createEmptyContactsIdsList(),
        page: 1,
        recordsOnPage: recordsOnPage || contactsRecordsOnPage(),
        onlyHumans: !!onlyHumans,
        applyUsersListFilters: !!applyUsersListFilters,
    };

    useEffect(() => {
        if (uuid === null) {
            return;
        }

        updateSearchInputData(uuid, defaultData);
    }, [uuid]);

    const searchInputData = storeSearchInputData ?? { ...defaultData };
    const setSearchInProgress = (state) => updateSearchInputData(uuid, { requestInProgress: state });

    useEffect(() => {
        // load the initially selected contact
        if (selectedContactId) {
            if (contactType === 'subscribers') {
                // show the preloader while the details of the externally selected user are loaded
                setSearchInProgress(true);

                getUserDetails(selectedContactId).then((selectedUser) => {
                    setInternalSelectedContact(selectedUser.Msisdn);
                    setSearchInProgress(false);
                });
            }
        }
    }, [selectedContactId]);

    useEffect(() => {
        setHasError(renderContacts && mandatory && !internalSelectedContact && searchInputData.keyword.length == 0);
    }, [internalSelectedContact, searchInputData]);

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    if (!placeholder) {
        placeholder =
            contactType === 'subscribers' ? t('manage_user_groups_search_user') : t('manage_user_groups_search_group');
    }

    const hasResults = !contactsIdsListIsEmpty(searchInputData.matches);
    const hasNoResults = searchInputHasNoResults(searchInputData);
    const popoverIsOn = renderContacts && (hasResults || hasNoResults);

    const canResetSearch = () => {
        let result = false;

        do {
            if (searchInputData.keyword) {
                result = true;
                break;
            }

            if (!renderContacts) {
                break;
            }

            result = internalSelectedContact !== null;
        } while (false);

        return result;
    };

    const resetSearch = (callResetCallback = true) => {
        updateSearchInputData(uuid, {
            ...defaultData,
        });

        setKeyPressTimer(null);

        if (internalSelectedContact !== null) {
            setInternalSelectedContact(null);

            if (callResetCallback && onContactReset) {
                onContactReset();
            }
        }

        if (callResetCallback && onReset) {
            onReset();
        }

        if (keyPressTimer !== null) {
            clearTimeout(keyPressTimer);
        }
    };

    const onInputChange = (e) => {
        const val = e.target.value;

        updateSearchInputData(uuid, {
            keyword: val,
            userIsTyping: true,
        });

        if (keyPressTimer !== null) {
            clearTimeout(keyPressTimer);
        }

        if (val.length === 0) {
            resetSearch(false);
            return;
        }

        setKeyPressTimer(
            setTimeout(() => {
                requestSearchInputPage(uuid, 1);
            }, 1000),
        );
    };

    /**
     * The function called when the input works as selector.
     * @param {string|number} contactId
     */
    const onContactItemClick = (contactId) => {
        resetSearch();
        onContactSelected(getContact(contactId));
        setTimeout(() => {
            setInternalSelectedContact(contactId);
        });
    };

    /**
     * Render a contact item.
     * @param {string|number} contactId
     * @param {object} props
     */
    const renderContactItem = (contactId, props = {}) => {
        const contact = getContact(contactId);

        if (contact === null) {
            return '';
        }

        if (!contact.IsGroup) {
            return <ContactUserItem msisdn={contact.Msisdn} key={`user-match-${contact.Msisdn}`} {...props} />;
        }

        return <ContactGroupItem groupId={contact.GroupId} key={`group-match-${contact.GroupId}`} {...props} />;
    };

    const pagesCount = storeSearchInputData
        ? Math.ceil(storeSearchInputData.matches.totalRecordsCount / contactsRecordsOnPage())
        : 0;

    return (
        <div
            className={classNames({
                'contact-search-input-container': true,
                'with-error': hasError,
            })}
            ref={containerRef}
            data-uuid={uuid}
            data-test-id="contact-search-input"
        >
            <TextInput
                value={searchInputData.keyword}
                disabled={searchInputData.requestInProgress || disabled}
                onChange={onInputChange}
                icon={disabled ? '' : canResetSearch() ? 'search-clear' : 'search-general'}
                iconClick={canResetSearch() ? () => resetSearch() : null}
                placeholder={placeholder}
                handleCancel={() => resetSearch()}
                inputRef={inputRef}
                data-test-id="contact-search-input"
                buttonDataTestId="contact-search-cancel-button"
            />
            {renderContacts && internalSelectedContact && (
                <div className={'selected-contact'}>{renderContactItem(internalSelectedContact)}</div>
            )}
            {searchInputData.requestInProgress && <Preloader key={'preloader'} asOverlay />}
            {popoverIsOn && (
                <Box className={'contact-search-input-popover'} sx={{ width: containerRef.current.offsetWidth }}>
                    {renderContacts && hasNoResults && <label className={'user-msg'}>{t('js_all_no_results')}</label>}
                    {renderContacts && !contactsIdsListIsEmpty(searchInputData.matches) && (
                        <>
                            <div className={`matches-container`}>
                                {searchInputData.matches.groupsIds
                                    .concat(searchInputData.matches.msisdns)
                                    .map((contactId) =>
                                        renderContactItem(contactId, {
                                            onClick: () => onContactItemClick(contactId),
                                        }),
                                    )}
                            </div>
                            {pagesCount > 1 && (
                                <div className="pagination-container">
                                    <Pagination
                                        color="primary"
                                        className={`${pagesCount > 5 ? 'expanded' : ''}`}
                                        count={pagesCount}
                                        page={storeSearchInputData.page}
                                        onChange={(event, page) => requestSearchInputPage(uuid, page)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Box>
            )}
        </div>
    );
}
